import { useEffect, useState } from 'react';
import {
  Paper,
  Toolbar,
  Stack,
  IconButton,
  Typography,
  Box,
  Divider,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { ArrowBack } from '@mui/icons-material';
import UndoIcon from '@mui/icons-material/Undo';
import { LoadingButton } from '@mui/lab';
import { useLocation, useNavigate } from 'react-router';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import {
  getChart,
  updateChart,
  createChart,
  rejectChart,
} from '@/actions/chart';
import { useApi } from '@/providers/ApiContext';
import ChartNameField from './ChartNameField';
import ChartDocumentTypeField from './ChartDocumentTypeField';
import { useUser } from '@/providers/UserContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import ChartReferenceDocumentField from './ChartReferenceDocumentField';
import ChartReferenceDocumentTypeField from './ChartReferenceDocumentTypeField';
import type { Chart, Document, DocumentType, EntityId } from '@/types';

export default function AdminChartForm(): React.JSX.Element {
  const location = useLocation();
  const api = useApi();
  const user = useUser();
  const doc_types = useDocTypes();
  const navigate = useNavigate();

  const [revision, setRevision] = useState<number>(0);
  const [pristine, setPristine] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loading_chart, setLoadingChart] = useState<boolean>(true);
  const [yanking, setYanking] = useState<boolean>(false);
  const [chart, setChart] = useState<Chart | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [document_type, setDocumentType] = useState<DocumentType | undefined>(
    undefined,
  );
  const [reference_document_type_options, setReferenceDocumentTypeOptions] =
    useState<DocumentType[]>([]);
  const [reference_document_type, setReferenceDocumentType] =
    useState<DocumentType | null>(null);
  const [reference_document, setReferenceDocument] = useState<Document | null>(
    null,
  );
  const [api_error, setApiError] = useState<string | null>(null);
  const sp = new URLSearchParams(location.search);
  const chart_id = sp.get('chart_id');
  const type = sp.get('type');

  useEffect(() => {
    if (user?.entity_id && !chart && api) {
      if (chart_id) {
        getChart(api, chart_id)
          .then((data) => {
            setChart(data);
            setName(data?.name || null);
            setReferenceDocument(
              (data as Chart & { reference_document?: Document | null })
                ?.reference_document || null,
            );
            setLoadingChart(false);
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load chart for editing',
                errorDetails:
                  (error as Error).message ||
                  'Unable to retrieve the chart for editing. This could be due to network issues, server problems, or the chart may no longer exist.',
                returnPath: `/admin/charts/${chart_id}`,
              },
            });
          });
      } else {
        setLoadingChart(false);
      }
    }
  }, [api, user?.entity_id, chart, chart_id, navigate]);

  useEffect(() => {
    if (doc_types && !document_type) {
      if (chart_id) {
        if (chart) {
          const doc_type = doc_types.find(
            (doc_type) => doc_type.entity_id === chart.document_type_id,
          );
          setDocumentType(doc_type);
        }
      } else if (type && type !== 'pending') {
        const doc_type = doc_types.find((doc_type) => doc_type.code === type);
        setDocumentType(doc_type);
      } else {
        const maxLevel = Math.max(
          ...doc_types.map((doc_type) => doc_type.level),
        );
        const filteredArray = doc_types.filter(
          (doc_type) => doc_type.level === maxLevel,
        );
        setDocumentType(filteredArray[0]);
      }
    }
  }, [document_type, type, chart, chart_id, doc_types]);

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [
    chart,
    doc_types,
    document_type,
    reference_document,
    reference_document_type,
  ]);

  useEffect(() => {
    if (document_type && doc_types) {
      const filteredArray = doc_types.filter(
        (doc_type) => doc_type.level < document_type.level,
      );
      setReferenceDocumentTypeOptions(filteredArray);
    }
  }, [document_type, doc_types]);

  const handleSubmit = async (): Promise<void> => {
    if (!document_type || !name || !api) return;
    setLoading(true);

    try {
      if (chart && chart_id) {
        const payload: {
          name?: string;
          document_type_id?: EntityId;
          reference_document_id?: EntityId;
        } = {};
        if (name) payload['name'] = name;
        if (document_type)
          payload['document_type_id'] = document_type.entity_id;
        if (reference_document)
          payload['reference_document_id'] = reference_document.entity_id;
        await updateChart(api, chart_id, payload);
      } else {
        // Create case - document_type_id and name are required
        const payload: {
          document_type_id: EntityId;
          name: string;
          reference_document_id?: EntityId;
        } = {
          document_type_id: document_type.entity_id,
          name,
        };
        if (reference_document)
          payload.reference_document_id = reference_document.entity_id;
        await createChart(api, payload);
      }
      if (document_type) {
        navigate({
          pathname: '/admin/charts',
          search: `?type=${document_type.code}`,
        });
      }
    } catch (error) {
      console.error('Error saving chart:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to save the chart. This could be due to network issues, server problems, or invalid data.',
      );
      setLoading(false);
    }
  };

  const handleYank = async (): Promise<void> => {
    if (!chart_id || !document_type || !api) return;
    setYanking(true);
    try {
      await rejectChart(api, chart_id);
      navigate({
        pathname: '/admin/charts',
        search: `?type=${document_type.code}`,
      });
    } catch (error) {
      console.error('Error yanking chart:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to yank the chart. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setYanking(false);
    }
  };

  if (loading_chart) {
    return <LoadingMask />;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
          <Toolbar
            sx={{
              minHeight: '55px !important',
            }}
          >
            <Stack spacing={2} direction='row' alignItems='center'>
              <IconButton onClick={() => navigate(-1)} disabled={loading}>
                <ArrowBack />
              </IconButton>
              <Typography variant='h5'>
                {chart ? `Edit ${chart.name}` : `Create New Chart`}
              </Typography>
            </Stack>
          </Toolbar>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <ChartDocumentTypeField
                key={`document_type_${revision}`}
                value={document_type || null}
                setValue={(value) => setDocumentType(value || undefined)}
                setChanged={() => setPristine(false)}
                edit={Boolean(chart)}
              />
            </Grid>
            {document_type && !Boolean(chart) ? (
              <Grid size={12}>
                <ChartReferenceDocumentTypeField
                  key={`reference_document_type_${revision}`}
                  options={reference_document_type_options}
                  value={reference_document_type}
                  setValue={setReferenceDocumentType}
                  edit={Boolean(chart)}
                />
              </Grid>
            ) : undefined}
            {reference_document_type || Boolean(chart) ? (
              <Grid size={12}>
                <ChartReferenceDocumentField
                  key={`reference_document_${revision}`}
                  document_type_id={
                    reference_document_type?.entity_id ||
                    (
                      chart as Chart & {
                        reference_document?: { document_type_id?: EntityId };
                      }
                    )?.reference_document?.document_type_id ||
                    ''
                  }
                  value={reference_document}
                  setValue={(doc) => {
                    setReferenceDocument(doc);
                    if (doc && (!name || name === '')) {
                      setName(`${doc.doc_id || ''} - ${doc.name}`);
                      setNameError(false);
                    }
                  }}
                  setChanged={() => setPristine(false)}
                  edit={Boolean(chart)}
                />
              </Grid>
            ) : undefined}

            <Grid size={12}>
              <ChartNameField
                key={`name_${revision}`}
                value={name || ''}
                error={name_error}
                setValue={setName}
                setError={setNameError}
                setChanged={() => setPristine(false)}
              />
            </Grid>

            {chart?.status === 'active' && (
              <Grid size={12}>
                <LoadingButton
                  variant='outlined'
                  color='error'
                  onClick={handleYank}
                  loading={yanking}
                  disabled={yanking}
                  startIcon={<UndoIcon />}
                >
                  Yank
                </LoadingButton>
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>

      <Grid size={12}>
        <Divider />
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <LoadingButton
              variant='contained'
              onClick={handleSubmit}
              loading={loading}
              disabled={
                pristine ||
                Boolean(name_error) ||
                !Boolean(name) ||
                !Boolean(document_type) ||
                !Boolean(reference_document)
              }
            >
              Submit
            </LoadingButton>

            <Button
              variant='outlined'
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </Grid>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Grid>
  );
}
